import React, { useState, useEffect, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  UserIcon,
  ArrowPathIcon,
  ChartBarIcon,
  SignalIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  BellIcon
} from '@heroicons/react/24/outline';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const FontLoader = () => (
  <link
    rel="stylesheet"
    href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Roboto:wght@300;400;500;700&display=swap"
  />
);

const LeafletMap = ({ employee, locationHistory }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const polylineRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || !employee?.latitude || !employee?.longitude) return;

    // Initialize map only once
    if (!mapInstanceRef.current) {
      try {
        mapInstanceRef.current = L.map(mapRef.current, {
          center: [employee.latitude, employee.longitude],
          zoom: 18
        });
        
        L.tileLayer('https://tile.openstreetmap.de/tiles/osmde/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 18,
          minZoom: 4
        }).addTo(mapInstanceRef.current);
      } catch (error) {
        console.error('Map initialization error:', error);
        return;
      }
    }

    const map = mapInstanceRef.current;

    // Update marker
    if (markerRef.current) {
      markerRef.current.setLatLng([employee.latitude, employee.longitude])
        .setPopupContent(`<strong>${employee.employeeName}</strong><br/>ID: ${employee.employeeId}<br/>Lat: ${employee.latitude.toFixed(5)}, Lng: ${employee.longitude.toFixed(5)}`);
    } else {
      markerRef.current = L.marker([employee.latitude, employee.longitude])
        .bindPopup(`<strong>${employee.employeeName}</strong><br/>ID: ${employee.employeeId}<br/>Lat: ${employee.latitude.toFixed(5)}, Lng: ${employee.longitude.toFixed(5)}`)
        .addTo(map)
        .openPopup();
    }

    // Update polyline
    if (polylineRef.current) {
      map.removeLayer(polylineRef.current);
      polylineRef.current = null;
    }

    if (locationHistory.length > 0) {
      const path = locationHistory.map(loc => [loc.lat, loc.lng]);
      polylineRef.current = L.polyline(path, { color: '#4f46e5', weight: 2, opacity: 0.7 }).addTo(map);
      
      const allPoints = [...path, [employee.latitude, employee.longitude]];
      const bounds = L.latLngBounds(allPoints);
      map.fitBounds(bounds, { padding: [50, 50] });
    } else {
      map.setView([employee.latitude, employee.longitude], 18);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      markerRef.current = null;
      polylineRef.current = null;
    };
  }, [employee?.employeeId, employee?.latitude, employee?.longitude, locationHistory]);

  return <div ref={mapRef} className="w-full h-full rounded-2xl" />;
};

const getStatusConfig = (status) => {
  const configs = {
    'Active': { bg: 'bg-green-100', text: 'text-green-700', badge: 'bg-green-500', label: 'Active' },
    'Idle': { bg: 'bg-yellow-100', text: 'text-yellow-700', badge: 'bg-yellow-500', label: 'Idle' },
    'Offline': { bg: 'bg-red-100', text: 'text-red-700', badge: 'bg-red-500', label: 'Offline' },
    'Break': { bg: 'bg-blue-100', text: 'text-blue-700', badge: 'bg-blue-500', label: 'On Break' }
  };
  return configs[status] || configs['Offline'];
};

const AlertNotification = ({ alert, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 6000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const getAlertConfig = () => {
    if (alert.title?.includes('OFFLINE') || alert.message?.toLowerCase().includes('offline')) {
      return { bg: 'bg-red-50 border-red-200', text: 'text-red-800', icon: 'text-red-600' };
    } else if (alert.title?.includes('IDLE') || alert.message?.toLowerCase().includes('idle')) {
      return { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-800', icon: 'text-yellow-600' };
    } else if (alert.title?.includes('PAUSED') || alert.message?.toLowerCase().includes('paused')) {
      return { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-800', icon: 'text-blue-600' };
    } else if (alert.title?.includes('STOPPED') || alert.message?.toLowerCase().includes('stopped')) {
      return { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-800', icon: 'text-gray-600' };
    }
    return { bg: 'bg-green-50 border-green-200', text: 'text-green-800', icon: 'text-green-600' };
  };

  const config = getAlertConfig();

  return (
    <div className={`fixed top-20 right-4 ${config.bg} border rounded-lg p-4 shadow-lg z-40 max-w-sm`}>
      <div className="flex items-start gap-3">
        <ExclamationCircleIcon className={`w-5 h-5 ${config.icon} flex-shrink-0 mt-0.5`} />
        <div className="flex-1">
          <p className={`font-bold ${config.text}`}>{alert.title}</p>
          <p className={`text-sm ${config.text} mt-1`}>{alert.message}</p>
          <p className={`text-xs ${config.text} opacity-70 mt-1`}>{new Date(alert.created_at).toLocaleTimeString()}</p>
        </div>
      </div>
    </div>
  );
};

const LiveTrackingDashboard = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('live');
  const [locationHistory, setLocationHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [showBellDropdown, setShowBellDropdown] = useState(false);

  // Fetch employees with live tracking
  useEffect(() => {
    const fetchLiveEmployees = async () => {
      try {
        setLoading(true);
        const response = await fetch('/tracking/employees/live');
        const data = await response.json();
        const empList = Array.isArray(data) ? data : [];
        setEmployees(empList);

        if (empList.length > 0 && !selectedEmployee) {
          setSelectedEmployee(empList[0]);
        }
      } catch (error) {
        console.error('Failed to fetch live employees:', error);
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLiveEmployees();
    const interval = setInterval(fetchLiveEmployees, 10000);
    return () => clearInterval(interval);
  }, [selectedEmployee]);

  // Fetch location history for selected employee
  useEffect(() => {
    const fetchHistory = async () => {
      if (selectedEmployee) {
        try {
          const response = await fetch(`/tracking/location/history/${selectedEmployee.employeeId}`);
          const data = await response.json();
          setLocationHistory(data.history || []);
        } catch (error) {
          console.error('Failed to fetch history:', error);
          setLocationHistory([]);
        }
      }
    };
    fetchHistory();
  }, [selectedEmployee]);

  // Fetch all alerts (status changes + pause/stop notifications)
  useEffect(() => {
    const fetchAllAlerts = async () => {
      if (!selectedEmployee) return;
      try {
        const [statusResponse, pauseStopResponse] = await Promise.all([
          fetch(`/alerts/employee/${selectedEmployee.employeeId}`),
          fetch(`/tracking/pause-stop-history/${selectedEmployee.employeeId}`)
        ]);
        
        const statusData = await statusResponse.json();
        const pauseStopData = await pauseStopResponse.json();
        
        const allAlerts = [];
        
        // Add status change alerts
        if (statusData.alerts) {
          statusData.alerts.forEach((alert, idx) => {
            let emoji = '🟢';
            let statusText = 'Active';
            let message = alert.message;
            
            if (alert.message?.toLowerCase().includes('offline')) {
              emoji = '🔴';
              statusText = 'OFFLINE';
              message = `${selectedEmployee.employeeName} (${selectedEmployee.employeeId}) went OFFLINE`;
            } else if (alert.message?.toLowerCase().includes('idle')) {
              emoji = '🟡';
              statusText = 'IDLE';
              message = `${selectedEmployee.employeeName} (${selectedEmployee.employeeId}) is IDLE`;
            }
            
            allAlerts.push({
              id: `status-${alert.id || idx}`,
              title: `${emoji} ${statusText}`,
              message: message,
              priority: alert.priority,
              is_read: alert.is_read,
              created_at: alert.created_at,
              emp_code: selectedEmployee.employeeId,
              emoji: emoji
            });
          });
        }
        
        // Add pause/stop alerts with descriptions
        if (pauseStopData.history) {
          pauseStopData.history.forEach((record, idx) => {
            let emoji = '⏸️';
            let statusText = 'PAUSED';
            
            if (record.action === 'stop') {
              emoji = '⏹️';
              statusText = 'STOPPED';
            }
            
            allAlerts.push({
              id: `pausestop-${record.id || idx}`,
              title: `${emoji} ${statusText}`,
              message: `${selectedEmployee.employeeName} (${selectedEmployee.employeeId}) ${statusText.toLowerCase()} tracking - Reason: ${record.description}`,
              priority: 'high',
              is_read: false,
              created_at: record.timestamp,
              emp_code: selectedEmployee.employeeId,
              emoji: emoji
            });
          });
        }
        
        // Sort by date descending
        allAlerts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setAlerts(allAlerts);
      } catch (error) {
        console.error('Failed to fetch alerts:', error);
      }
    };

    fetchAllAlerts();
    const interval = setInterval(fetchAllAlerts, 5000);
    return () => clearInterval(interval);
  }, [selectedEmployee]);

  // Fetch analytics
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (selectedEmployee && activeView === 'analytics') {
        try {
          const response = await fetch(`/tracking/employee/${selectedEmployee.employeeId}/analytics?period=7d`);
          const data = await response.json();
          setAnalytics(data);
        } catch (error) {
          console.error('Failed to fetch analytics:', error);
        }
      }
    };
    fetchAnalytics();
  }, [selectedEmployee, activeView]);

  const filteredEmployees = employees.filter(emp => 
    emp.employeeId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.employeeName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading && employees.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center font-['Poppins']">
        <div className="flex flex-col items-center">
          <ArrowPathIcon className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
          <p className="text-slate-500 font-medium">Loading live tracking data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-['Poppins'] text-slate-900">
      <FontLoader />
      
      {/* Top Navigation Bar */}
      <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-8 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-600 p-2 rounded-lg">
            <SignalIcon className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-xl font-bold tracking-tight text-slate-800">
            Field<span className="text-indigo-600">Track</span> Pro
          </h1>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <button 
              onClick={() => setShowBellDropdown(!showBellDropdown)}
              className="relative p-2 hover:bg-slate-100 rounded-lg transition"
            >
              <BellIcon className={`w-5 h-5 ${alerts.length > 0 ? 'text-red-600' : 'text-slate-400'}`} />
              {alerts.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {alerts.length}
                </span>
              )}
            </button>
            
            {showBellDropdown && alerts.length > 0 && (
              <div className="absolute right-0 top-12 w-96 bg-white border border-slate-200 rounded-lg shadow-2xl z-50 max-h-96 overflow-hidden flex flex-col">
                <div className="p-4 border-b border-slate-100 bg-slate-50 sticky top-0">
                  <p className="text-sm font-bold text-slate-700">Notifications ({alerts.length})</p>
                </div>
                <div className="overflow-y-auto divide-y divide-slate-100">
                  {alerts.map(alert => (
                    <div key={alert.id} className="p-4 hover:bg-slate-50 transition cursor-pointer">
                      <p className="text-sm font-bold text-slate-800">{alert.title}</p>
                      <p className="text-xs text-slate-600 mt-1 line-clamp-2">{alert.message}</p>
                      <p className="text-xs text-slate-400 mt-2">{new Date(alert.created_at).toLocaleTimeString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <nav className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
            {[
              { id: 'live', label: 'Live Map', icon: <MapPinIcon className="w-4 h-4" /> },
              { id: 'history', label: 'History', icon: <CalendarDaysIcon className="w-4 h-4" /> },
              { id: 'analytics', label: 'Analytics', icon: <ChartBarIcon className="w-4 h-4" /> }
            ].map((view) => (
              <button
                key={view.id}
                onClick={() => setActiveView(view.id)}
                className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                  activeView === view.id
                    ? 'bg-white text-indigo-600 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {view.icon}
                {view.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar: Employee List */}
        <aside className="w-80 bg-white border-r border-slate-200 flex flex-col">
          <div className="p-4 border-b border-slate-100">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all text-sm font-['Roboto']"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {filteredEmployees.length === 0 ? (
              <div className="p-10 text-center">
                <UserIcon className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500 font-medium">No active tracks</p>
              </div>
            ) : (
              filteredEmployees.map((employee) => {
                const statusConfig = getStatusConfig(employee.status);
                return (
                  <div
                    key={employee.employeeId}
                    onClick={() => setSelectedEmployee(employee)}
                    className={`group p-4 border-b border-slate-50 cursor-pointer flex items-center justify-between transition-colors ${
                      selectedEmployee?.employeeId === employee.employeeId
                        ? 'bg-indigo-50/50 border-r-4 border-r-indigo-600'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold text-sm">
                          {employee.employeeName.charAt(0)}
                        </div>
                        <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${statusConfig.badge}`} />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-800 leading-tight">{employee.employeeName}</h3>
                        <p className="text-xs text-slate-500 font-['Roboto']">{employee.employeeId}</p>
                      </div>
                    </div>
                    <ChevronRightIcon className={`w-4 h-4 text-slate-300 group-hover:text-indigo-400 transition-transform ${
                      selectedEmployee?.employeeId === employee.employeeId ? 'translate-x-1 text-indigo-500' : ''
                    }`} />
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {/* Main Display Area */}
        <main className="flex-1 overflow-y-auto bg-slate-50 p-6 custom-scrollbar">
          <AnimateView 
            view={activeView} 
            selectedEmployee={selectedEmployee} 
            locationHistory={locationHistory}
            analytics={analytics}
          />
        </main>
      </div>
    </div>
  );
};

const AnimateView = ({ view, selectedEmployee, locationHistory, analytics }) => {
  if (!selectedEmployee) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-400">
        <UserIcon className="w-16 h-16 mb-4 opacity-20" />
        <p className="text-lg font-medium">Select an employee to view details</p>
      </div>
    );
  }

  const statusConfig = getStatusConfig(selectedEmployee.status);

  switch (view) {
    case 'live':
      return (
        <div className="h-full flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-800">Live Status</h2>
            <span className={`flex items-center gap-2 px-3 py-1 ${statusConfig.bg} ${statusConfig.text} rounded-full text-xs font-bold uppercase tracking-wider`}>
              <span className={`w-2 h-2 ${statusConfig.badge} rounded-full ${selectedEmployee.status === 'Active' ? 'animate-ping' : ''}`} />
              {statusConfig.label}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatusCard label="Current Status" value={selectedEmployee.status} status={selectedEmployee.status} />
            <InfoCard label="Last Signal" value={selectedEmployee.last_ping} sub="Device timestamp" />
            <InfoCard 
                label="Coordinates" 
                value={selectedEmployee.latitude ? `${selectedEmployee.latitude.toFixed(4)}, ${selectedEmployee.longitude.toFixed(4)}` : 'N/A'} 
                sub="GPS Lat/Long" 
                mono 
            />
          </div>

          <div className="flex-1 bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <LeafletMap employee={selectedEmployee} locationHistory={locationHistory} />
          </div>
        </div>
      );

    case 'history':
      return (
        <div className="flex flex-col gap-6 h-full">
          <h2 className="text-2xl font-bold text-slate-800">Movement History - {selectedEmployee.employeeName}</h2>
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden flex flex-col">
            <div className="overflow-x-auto">
              <table className="w-full text-left font-['Roboto']">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr className="text-slate-500 text-xs uppercase font-bold tracking-wider">
                    <th className="px-6 py-4">Timestamp</th>
                    <th className="px-6 py-4">Location</th>
                    <th className="px-6 py-4 text-center">Speed</th>
                    <th className="px-6 py-4 text-right">Accuracy</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {locationHistory.length > 0 ? (
                    locationHistory.map((loc, i) => (
                      <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {new Date(loc.timestamp).toLocaleString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: 'short' })}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-800">
                          {loc.lat.toFixed(5)}, {loc.lng.toFixed(5)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="bg-indigo-50 text-indigo-600 px-2 py-1 rounded-md text-xs font-bold">
                            {loc.speed?.toFixed(1) || '0.0'} km/h
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-slate-400">
                          ±{loc.accuracy || 10}m
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-20 text-center text-slate-400">No records found for this period</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );

    case 'analytics':
      return (
        <div className="flex flex-col gap-6">
          <h2 className="text-2xl font-bold text-slate-800">Insights & Activity</h2>
          
          {analytics ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 space-y-6">
                <SummaryCard title="Total Pings" value={analytics.summary?.total_pings || 0} icon={<SignalIcon />} color="indigo" />
                <SummaryCard title="Hotspots" value={analytics.heat_zones?.length || 0} icon={<MapPinIcon />} color="orange" />
                <SummaryCard title="Active Days" value={analytics.daily_stats?.length || 0} icon={<CalendarDaysIcon />} color="green" />
              </div>

              <div className="lg:col-span-2 bg-white p-6 border border-slate-200 rounded-2xl shadow-sm">
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-6">Device Activity Log (7D)</h3>
                <div className="space-y-6">
                  {analytics.daily_stats?.slice(0, 7).map((day, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-xs font-bold text-slate-400 mb-2 uppercase font-['Roboto']">
                        <span>{new Date(day.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
                        <span className="text-indigo-600">{day.pings} signals</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                        <div 
                          className="bg-indigo-500 h-full rounded-full transition-all duration-1000" 
                          style={{ width: `${Math.min((day.pings / 100) * 100, 100)}%` }} 
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white p-20 border border-slate-200 rounded-2xl text-center">
              <ArrowPathIcon className="w-8 h-8 animate-spin mx-auto text-indigo-300 mb-2" />
              <p className="text-slate-400 text-sm">Generating report...</p>
            </div>
          )}
        </div>
      );

    default:
      return null;
  }
};

const StatusCard = ({ label, value, status }) => {
  const statusConfig = getStatusConfig(status);
  return (
    <div className={`${statusConfig.bg} p-5 border border-slate-200 rounded-xl shadow-sm`}>
      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-lg font-bold ${statusConfig.text}`}>{value}</p>
      <p className="text-[10px] text-slate-400 mt-2 italic">Tracking status</p>
    </div>
  );
};

const InfoCard = ({ label, value, sub, mono }) => (
  <div className="bg-white p-5 border border-slate-200 rounded-xl shadow-sm">
    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
    <p className={`text-lg font-bold text-slate-800 ${mono ? "font-['Roboto']" : ""}`}>{value}</p>
    <p className="text-[10px] text-slate-400 mt-2 italic">{sub}</p>
  </div>
);

const SummaryCard = ({ title, value, icon, color }) => {
    const colors = {
        indigo: 'bg-indigo-600',
        orange: 'bg-orange-500',
        green: 'bg-emerald-500'
    };
    return (
        <div className="bg-white p-6 border border-slate-200 rounded-2xl flex items-center gap-5 shadow-sm">
            <div className={`${colors[color]} p-3 rounded-xl text-white`}>
                {React.cloneElement(icon, { className: "w-6 h-6" })}
            </div>
            <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{title}</p>
                <p className="text-3xl font-bold text-slate-800 font-['Roboto']">{value}</p>
            </div>
        </div>
    );
};

export default LiveTrackingDashboard;
